import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {selectOrderForm, selectOrderStatus} from '@app/checkout/state/checkout.selector';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {loadCountries} from '@app/signUp/state/sign-up.actions';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {Bind} from 'primeng/bind';
import {ConfirmDialog} from 'primeng/confirmdialog';
import {Message} from 'primeng/message';
import {ProgressSpinner} from 'primeng/progressspinner';
import {Subject, takeUntil, tap} from 'rxjs';
import {CheckoutStepperComponent} from '../../components/checkout-stepper/checkout-stepper.component';
import {Order} from '../../models/order-request.model';
import {getOrder, resetGetOrderStatus} from '../../state/checkout.actions';

@Component({
  selector: 'app-checkout-page',
  templateUrl: './checkout-page.component.html',
  styleUrls: ['./checkout-page.component.scss'],
  imports: [CheckoutStepperComponent, Bind, Message, ConfirmDialog, TranslatePipe, ProgressSpinner],
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  orderData: Order | undefined;
  orderId = 0;
  selectedOrderItemIndex = 0;
  private readonly unsubscribe$ = new Subject<void>();
  countryId!: number;
  isOrderLoading = false;
  constructor() {
    this.getOrder();
  }

  ngOnInit(): void {
    this.store.dispatch(resetGetOrderStatus());
    this.initializeOrderFormData();
    this.getOrderStatus();
    this.store.dispatch(loadCountries());
  }

  private getOrder() {
    this.route.queryParams?.subscribe(params => {
      if (params['id'] && params['id'] > 0) {
        this.orderId = params['id'];
        this.isOrderLoading = true;
        this.store.dispatch(getOrder({orderId: this.orderId}));
      }
    });
  }

  getOrderItemAttribute(key: string) {
    if (this.orderData?.orderItems?.length && this.orderData?.orderItems[0].orderItemAttributes?.length) {
      return ObjectUtils.getValueByDotSeparatedKey(key, this.orderData?.orderItems[0].orderItemAttributes[0]);
    }
    return '';
  }

  isActive(orderData: Order | undefined) {
    const selectedOrderItem = orderData?.orderItems?.length ? orderData?.orderItems[this.selectedOrderItemIndex] : null;
    const selectedOrderItemAttribute = selectedOrderItem?.orderItemAttributes?.find(
      x => x.orderItemId == selectedOrderItem?.id
    );
    return selectedOrderItem?.isActive && selectedOrderItemAttribute?.canBeOrdered;
  }

  private initializeOrderFormData() {
    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          if (order.id && order.orderStatus?.id === OrderStatusEnum.Confirmed) {
            this.router.navigate(['/my-orders']);
          } else if (order.id && order.orderStatus?.id !== OrderStatusEnum.New) {
            this.router.navigate(['/my-orders/order-details'], {
              queryParams: {orderId: order.id},
            });
          }
          this.orderData = order;
        })
      )
      .subscribe();
  }

  private getOrderStatus() {
    this.store
      .select(selectOrderStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(orderStatus => {
          if (orderStatus === EActionStatus.Success) {
            this.isOrderLoading = false;
            return;
          }

          if (orderStatus === EActionStatus.Failure) {
            this.isOrderLoading = false;
            this.router.navigate(['/my-orders']);
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
